import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, Injectable, NgModule } from '@angular/core';
import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig,
  HammerModule,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OryAuthEffects, oryAuthFeatureKey, oryAuthReducer } from '@ory-auth';
import * as Hammer from 'hammerjs';
import { IConfig, provideEnvironmentNgxMask } from 'ngx-mask';

import { RoutingHistoryService } from '@aw/shared/services/routing-history';

import { ENV, Environment } from '@aw/prypco/environment';
import { DialogServiceModule } from '@aw/prypco/services/dialog';
import { ReferenceDataService } from '@aw/prypco/services/reference-data';
import {
  AmplitudeNoopService,
  AmplitudeService,
  AmplitudeServiceImpl,
  GtmService,
  GtmServiceImpl,
} from '@aw/prypco/services/script-manager';
import { SnackBarModule } from '@aw/prypco/services/snackbar';
import { AuthFacade, AuthStateModule } from '@aw/prypco/state/auth';
import { EventStateModule } from '@aw/prypco/state/events';
import { SocketStateModule } from '@aw/prypco/state/socket';
import { SplashScreenStateModule } from '@aw/prypco/state/splash-screen';

import { environment } from '../environments/environment';
import { appInitializer } from './app-initializer.util';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FirebaseRemoteConfigService } from '../firebase/data-access/remote-config-firebase.service';

@Injectable()
class HammerConfig extends HammerGestureConfig {
  override overrides = <any>{
    swipe: { direction: Hammer.DIRECTION_HORIZONTAL },
    pinch: { enable: false },
    rotate: { enable: false },
    pan: { enable: false },
  };
}

const maskConfig: Partial<IConfig> = {};

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SnackBarModule,
    DialogServiceModule,
    AppRoutingModule,
    // Start of NGRX
    StoreModule.forRoot(
      {},
      {
        metaReducers: !environment.production ? [] : [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      },
    ),
    EffectsModule.forRoot([]),
    StoreModule.forFeature(oryAuthFeatureKey, oryAuthReducer),
    EffectsModule.forFeature([OryAuthEffects]),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AuthStateModule,
    SplashScreenStateModule,
    EventStateModule,
    SocketStateModule,
    // End of NGRX
    HammerModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [ReferenceDataService, AuthFacade, FirebaseRemoteConfigService],
      multi: true,
    },
    {
      provide: ENV,
      useValue: { ...environment },
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
    {
      provide: AmplitudeService,
      useFactory: (env: Environment) =>
        env.externalScripts.amplitude.enabled
          ? new AmplitudeServiceImpl()
          : new AmplitudeNoopService(),
      deps: [ENV],
    },
    {
      provide: GtmService,
      useFactory: () => new GtmServiceImpl(),
    },
    RoutingHistoryService,
    provideEnvironmentNgxMask(maskConfig),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(private amplitudeService: AmplitudeService) {}
}
